import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client/react'
import {
  ConfirmOrgTicketAttachmentDocument,
  OrgTicketAttachmentUrlDocument,
  OrgTicketDocument,
  PresignOrgTicketAttachmentDocument,
} from '@/graphql/generated/graphql'

/**
 * Flujo de adjuntos del goal reportes-05 sobre la superficie del portal:
 * presign (gateway) → PUT directo a MinIO → confirm (verifica el mime real).
 * Bucket PRIVADO: la descarga es siempre una URL firmada fresca (lazy query),
 * nunca una URL persistida. El `code` viaja por llamada para poder subir
 * contra un ticket recién creado (decisión 8: se presigna SIEMPRE contra un
 * ticket existente).
 */
export function useOrgTicketAttachments() {
  const client = useApolloClient()
  const [presign] = useMutation(PresignOrgTicketAttachmentDocument)
  const [confirm] = useMutation(ConfirmOrgTicketAttachmentDocument)
  const [fetchUrl] = useLazyQuery(OrgTicketAttachmentUrlDocument, {
    // Siempre fresca: la URL firmada caduca (~1h) y no debe venir de caché.
    fetchPolicy: 'network-only',
  })

  const uploadAttachment = async (code: string, file: File): Promise<void> => {
    const presigned = await presign({
      variables: {
        input: {
          code,
          name: file.name,
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
        },
      },
    })
    const data = presigned.data?.presignOrgTicketAttachment
    if (!data) throw new Error('No se pudo preparar la subida del adjunto')

    const put = await fetch(data.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    })
    if (!put.ok) {
      throw new Error(`La subida del adjunto falló (HTTP ${put.status})`)
    }

    await confirm({
      variables: { input: { code, attachmentId: data.attachmentId } },
    })
    // Refresca el detalle si está en caché (galería de adjuntos al día).
    await client
      .refetchQueries({ include: [OrgTicketDocument] })
      .catch(() => undefined)
  }

  const openAttachment = async (
    code: string,
    attachmentId: string,
  ): Promise<void> => {
    const res = await fetchUrl({ variables: { code, attachmentId } })
    const url = res.data?.orgTicketAttachmentUrl.url
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }

  return { uploadAttachment, openAttachment }
}
